import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X, Plane, CircleAlert } from '@/components/icons'
import { Button } from '../ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '../ui/drawer'
import { PrntrCard } from '../ui/prntr-input'
import { PickerRowField, TextRow } from '../ui/prntr-fields'
import { AirportPickerDrawer } from '../AirportPickerDrawer'
import { toast } from 'sonner'
import { track, EVENTS } from '@/utils/analytics'
import { useAircraftCreate } from '../../lib/hooks'
import { useAirportByIcao } from '../../lib/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../../lib/orpc'

const quickAircraftSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  registration: z.string().min(1, 'Tail number is required'),
  baseAirportIcao: z.string().min(1, 'Base is required'),
})

type QuickAircraftForm = z.infer<typeof quickAircraftSchema>

interface AddedAircraft {
  name: string
  registration: string
}

interface QuickStartAircraftDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function QuickStartAircraftDrawer({ isOpen, onClose }: QuickStartAircraftDrawerProps) {
  const [addedAircraft, setAddedAircraft] = useState<AddedAircraft[]>([])
  const [isAirportPickerOpen, setIsAirportPickerOpen] = useState(false)
  const createAircraft = useAircraftCreate()
  const queryClient = useQueryClient()
  const orpc = useOrpcTanstackQueryUtils()

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isValid, isSubmitting },
  } = useForm<QuickAircraftForm>({
    resolver: zodResolver(quickAircraftSchema),
    defaultValues: {
      name: '',
      registration: '',
      baseAirportIcao: '',
    },
    mode: 'onChange',
  })

  const watchBaseAirportIcao = watch('baseAirportIcao')
  const selectedAirport = useAirportByIcao(watchBaseAirportIcao)

  const handleAirportSelect = (icaoCode: string) => {
    setValue('baseAirportIcao', icaoCode, { shouldValidate: true })
    setIsAirportPickerOpen(false)
  }

  const handleSave = async (data: QuickAircraftForm, addAnother: boolean) => {
    try {
      await createAircraft.mutateAsync({
        type: data.name,
        registration: data.registration,
        baseAirportIcao: data.baseAirportIcao,
        manufacturer: '',
        maxPassengers: 0,
        crewCapacity: 0,
      })
      track(EVENTS.AIRCRAFT_CREATED)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add aircraft')
      return
    }

    setAddedAircraft((prev) => [...prev, { name: data.name, registration: data.registration }])

    if (addAnother) {
      reset()
    } else {
      queryClient.invalidateQueries({ queryKey: orpc.onboarding.key() })
      handleClose()
    }
  }

  const handleClose = () => {
    reset()
    setAddedAircraft([])
    onClose()
  }

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
          <DrawerHeader className="flex items-center justify-between p-4 shrink-0">
            <DrawerTitle className="text-lg font-bold text-content-primary-bold tracking-[-1px]">
              Add Aircraft
            </DrawerTitle>
            <button type="button" onClick={handleClose} className="p-1 hover:bg-surface-L2 rounded transition-colors">
              <X className="w-4 h-4 text-content-primary-bold" />
            </button>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Info banner */}
            <div className="bg-surface-L2 rounded-md flex gap-3 items-center px-3 py-2 mb-2">
              <CircleAlert className="w-5 h-5 text-content-primary-subtle shrink-0" />
              <p className="text-sm text-content-primary-subtle tracking-[0.07px] leading-normal">
                You can add more details and upload documents later in the aircraft directory.
              </p>
            </div>

            {/* Added aircraft chips */}
            {addedAircraft.length > 0 && (
              <div className="flex flex-wrap gap-2 px-2 py-4">
                {addedAircraft.map((aircraft, i) => (
                  <div
                    key={i}
                    className="bg-surface-L2 rounded-full px-3 py-1 flex items-center gap-1.5"
                  >
                    <Plane className="w-3.5 h-3.5 text-content-primary-subtle" />
                    <span className="text-xs font-medium text-content-primary-bold">{aircraft.registration}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Form */}
            <div className="flex flex-col gap-3 p-2">
              <PrntrCard title="Aircraft">
                <TextRow nullable={false}
                  control={control}
                  name="name"
                  label="Name"
                  placeholder="e.g. Global 7500"
                  iconLeft={<Plane />}
                />
                <TextRow nullable={false}
                  control={control}
                  name="registration"
                  label="Tail Number"
                  placeholder="e.g. N787LD"
                />
                <PickerRowField
                  control={control}
                  name="baseAirportIcao"
                  label="Base"
                  display={(value) =>
                    value
                      ? selectedAirport
                        ? `${selectedAirport.icaoCode} · ${selectedAirport.name}`
                        : String(value)
                      : null
                  }
                  placeholder="Select airport"
                  onOpen={() => setIsAirportPickerOpen(true)}
                />
              </PrntrCard>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 shrink-0">
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="flex-1"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit((data) => handleSave(data, true))}
              >
                Save & Add Another
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="flex-1"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit((data) => handleSave(data, false))}
              >
                Save & Continue Setup
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <AirportPickerDrawer
        isOpen={isAirportPickerOpen}
        onClose={() => setIsAirportPickerOpen(false)}
        onSelect={handleAirportSelect}
        searchParam={watchBaseAirportIcao}
      />
    </>
  )
}
